import { isNil } from 'lodash';
import { nanoid } from 'nanoid';
import { Editor, Range, Transforms } from 'slate';

import {
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_H4,
  ELEMENT_H5,
  ELEMENT_H6,
  ELEMENT_PARAGRAPH,
} from '../../../../../slatePlugins';
import { ELEMENT_HIGHLIGHT } from '../../../../DolEditor/elements/highlight/defaults';
import { ELEMENT_ICON } from '../../../../DolEditor/elements/icons/defaults';
import { ELEMENT_ROUNDED } from '../../../../DolEditor/elements/rounded/defaults';
import {
  ELEMENT_BULLET_POINT,
  ELEMENT_BULLET_POINT_CHECK,
  ELEMENT_BULLET_POINT_LIST_ITEM,
  ELEMENT_BULLET_POINT_NUMBER,
  ELEMENT_BULLET_POINT_QUESTION,
  ELEMENT_BULLET_POINT_X,
} from '../BulletPointPlugin.types';

export const matchNormalBlockNode = (n: any) => {
  return [
    ELEMENT_PARAGRAPH,
    ELEMENT_H1,
    ELEMENT_H2,
    ELEMENT_H3,
    ELEMENT_H4,
    ELEMENT_H5,
    ELEMENT_H6,
    ELEMENT_ROUNDED,
    ELEMENT_HIGHLIGHT,
    ELEMENT_ICON,
  ].includes(n?.type);
};

export const matchBulletPoint = (n: any) =>
  [
    ELEMENT_BULLET_POINT,
    ELEMENT_BULLET_POINT_NUMBER,
    ELEMENT_BULLET_POINT_CHECK,
    ELEMENT_BULLET_POINT_X,
    ELEMENT_BULLET_POINT_QUESTION,
  ].includes(n?.type);

export const matchListItem = (n: any) => n.type === ELEMENT_BULLET_POINT_LIST_ITEM;

function currentActiveBulletPoint(editor) {
  const [match] = Editor.nodes(editor, {
    at: editor.selection,
    match: matchBulletPoint,
  });

  if (match) {
    const [node] = match as any;
    return node.type;
  }

  return null;
}

function unwrapList(editor) {
  Transforms.setNodes(editor, { type: ELEMENT_PARAGRAPH });
  Transforms.unwrapNodes(editor, { match: matchListItem });
  Transforms.unwrapNodes(editor, { match: matchBulletPoint, split: true });
}

function generateListNode(type) {
  return { id: nanoid(), type: type || ELEMENT_BULLET_POINT, children: [] };
}

function generateListItemNode(type) {
  return {
    type: ELEMENT_BULLET_POINT_LIST_ITEM,
    id: nanoid(),
    children: [],
  };
}

function wrapListItemOnParagraphNodes({ editor, allParagraphNodes, type }) {
  Editor.withoutNormalizing(editor, () => {
    allParagraphNodes.forEach((nodeEntry) => {
      const [, path] = nodeEntry;
      Transforms.wrapNodes(editor, generateListItemNode(type), { at: path });
      Transforms.wrapNodes(editor, generateListNode(type), { at: path });
    });
  });
}

function changeAllBulletPointNodesToNewType({ editor, allBulletPointsNodes, type }) {
  Editor.withoutNormalizing(editor, () => {
    allBulletPointsNodes.forEach((nodeEntry) => {
      const [, path] = nodeEntry;
      const bulletPointChilds = Editor.nodes(editor, { at: path, match: matchBulletPoint });
      Transforms.setNodes(editor, { type }, { at: path });
      if (bulletPointChilds) {
        for (const [, childPath] of bulletPointChilds) {
          Transforms.setNodes(editor, { type }, { at: childPath });
        }
      }
    });
  });
}

function disableAllBulletPointNodes({ editor, allBulletPointsNodes }) {
  Editor.withoutNormalizing(editor, () => {
    const allParagraphNodes = [];
    allBulletPointsNodes.forEach((nodeEntry) => {
      const [, path] = nodeEntry;
      const nodeEntries = Editor.nodes(editor, {
        at: path,
        match: matchNormalBlockNode,
      });
      for (const [node] of nodeEntries) {
        allParagraphNodes.push(node);
      }
      Transforms.delete(editor, { at: path });
      Transforms.insertNodes(editor, allParagraphNodes, { at: path });
    });
  });
}

function toggleListElement(editor, { type, forceInActive = false }) {
  const { selection } = editor;
  if (!selection) return;

  // toggle list by expanded range
  if (Range.isExpanded(editor.selection)) {
    const paragraphEntries = Editor.nodes(editor, {
      at: editor.selection,
      match: matchNormalBlockNode,
      mode: 'highest',
    });
    const bulletPointEntries = Editor.nodes(editor, {
      at: editor.selection,
      match: matchBulletPoint,
      mode: 'highest',
    });

    // only take the highest node
    const allBulletPointsNodes = Array.from(bulletPointEntries) as any[];
    const allParagraphNodes = (Array.from(paragraphEntries) as any[]).filter((paragraphEntry) => {
      let isChildOfBulletPoint = false;
      if (allBulletPointsNodes.length > 0) {
        isChildOfBulletPoint = allBulletPointsNodes.some((bulletPointEntry) => {
          return paragraphEntry[1].length > bulletPointEntry[1].length;
        });
      }
      return !isChildOfBulletPoint;
    });
    // 1. if there is at least one paragraph and one bullet point node => change all paragraph & bullet point to new type
    // note: bullet point need to change all their child ul to new type
    if (allParagraphNodes.length > 0 && allBulletPointsNodes.length > 0) {
      wrapListItemOnParagraphNodes({
        editor,
        allParagraphNodes,
        type,
      });
      changeAllBulletPointNodesToNewType({ editor, allBulletPointsNodes, type });
      return;
    }

    // 2. if none paragraph node AND there is at least one bullet point
    if (allParagraphNodes.length === 0 && allBulletPointsNodes.length > 0) {
      const isAllBulletPointSameWithNewType = allBulletPointsNodes.every((entry) => {
        const [node] = entry;
        return node.type === (type || ELEMENT_BULLET_POINT);
      });
      // 2.1 all bullet point are same with new type => inactive
      if (isAllBulletPointSameWithNewType) {
        disableAllBulletPointNodes({ editor, allBulletPointsNodes });
        return;
      }

      // 2.2 one of bulletpoint is different from new type => set to new type
      changeAllBulletPointNodesToNewType({ editor, allBulletPointsNodes, type });

      return;
    }

    // 3. if at least one paragraph node AND none bullet points => change to new types
    if (allParagraphNodes.length > 0 && allBulletPointsNodes.length === 0) {
      wrapListItemOnParagraphNodes({
        editor,
        allParagraphNodes,
        type,
      });
      return;
    }

    // 4. if none paragraph node and none bullet points => rare case - do nothing
    return;
  }

  Editor.withoutNormalizing(editor, () => {
    // toggle list by collapsed range
    const [match] = Editor.nodes(editor, {
      at: editor.selection,
      match: matchBulletPoint,
    });

    const isActive = !!match;
    if (isActive) {
      const [ulNode, ulPath] = match as any;
      if (!forceInActive && ulNode && ulNode.type !== type) {
        const matchEntries = Editor.nodes(editor, {
          at: ulPath,
          match: matchBulletPoint,
        });
        for (const [, path] of matchEntries) {
          Transforms.setNodes(editor, { type }, { at: path });
        }
        return;
      }
      const nodeEntries = Editor.nodes(editor, {
        at: ulPath,
        match: matchNormalBlockNode,
      });
      const allParagraphNodes = [];
      for (const [node] of nodeEntries) {
        const isValid = node?.children
          ? (node?.children || [])?.every((n) => (n?.type ? matchNormalBlockNode(n) : true))
          : true;
        if (isValid) {
          allParagraphNodes.push(node);
        }
      }
      Transforms.removeNodes(editor, { at: ulPath });
      Transforms.insertNodes(editor, allParagraphNodes, { at: ulPath });
    } else {
      unwrapList(editor);
      Transforms.setNodes(editor, { type: ELEMENT_PARAGRAPH });
      Transforms.wrapNodes(editor, generateListNode(type));
      const nodes = Editor.nodes(editor, {
        match: (n: any) =>
          n?.type === ELEMENT_PARAGRAPH && n?.children.every((node) => !isNil(node.text)),
      });
      try {
        for (const [, path] of nodes) {
          Transforms.wrapNodes(editor, generateListItemNode(type), {
            at: path,
          });
        }
        // eslint-disable-next-line no-empty
      } catch (ex) {}
    }
  });
}

export const BulletPointTransformActions = {
  toggleListElement,
  currentActiveBulletPoint,
};
